import React from "react";

import { a } from "@react-spring/web";

import { useIntersect } from "hooks/useIntersect";
import { useAnimateInTrail, IAnimateInProps } from "hooks/useAnimateInTrail";
import {
  initialOffset,
  IBackgroundShade,
} from "components/Animations/SpringProperties/SpringProperties";
import classNames from "classnames";

export interface IQuotation {
  name: string;
  title: string;
  quotation: JSX.Element;
}

interface IOwnSpecificProps {
  quotation: IQuotation;
  class?: string;
  viewportOffsets?: {
    top: number;
    bottom: number;
  };
  sectionBackgroundShade?: IBackgroundShade;
  quotationMarkColor?: string;
  personLineColor?: string;
}

type IOwnProps = IOwnSpecificProps & IAnimateInProps;

type IProps = IOwnProps;

export const FullQuotationComponent: React.FC<IProps> = ({
  animationReady,
  quotation,
  viewportOffsets = {
    top: -300,
    bottom: -450,
  },
  personLineColor,
  sectionBackgroundShade,
  quotationMarkColor,
  ...props
}) => {
  const [inViewRef, inView] = useIntersect({
    startObserving: animationReady,
    rootMargin:
      viewportOffsets.top + "px 0px " + viewportOffsets.bottom + "px 0px",
  });

  const [animateInTrail] = useAnimateInTrail(inView, 7);

  const markStyles = {
    opacity: animateInTrail[0].active,
    transform: animateInTrail[0].active.to(
      (x) => `translateY(calc(${initialOffset(x)}))`
    ),
    color: quotationMarkColor,
  };

  const titleStyles = {
    opacity: animateInTrail[1].active,
    transform: animateInTrail[1].active.to(
      (x) => `translateY(calc(${initialOffset(x)}))`
    ),
  };

  const detailsContainerStyles = {
    transform: animateInTrail[2].active.to(
      (x) => `translateY(calc(${initialOffset(x)}))`
    ),
  };

  const lineStyles = {
    opacity: animateInTrail[2].active,
    width: animateInTrail[2].active.to((x) => `calc(${x} * 100%)`),
    backgroundColor: personLineColor,
  };

  const nameStyles = {
    opacity: animateInTrail[2].active,
    transform: animateInTrail[2].active.to(
      (x) => `translateX(calc(-1 * (${initialOffset(x)})))`
    ),
  };

  const personTitleStyles = {
    opacity: animateInTrail[3].active,
    transform: animateInTrail[3].active.to(
      (x) => `translateX(calc(-1 * (${initialOffset(x)})))`
    ),
  };

  return (
    <div
      ref={inViewRef}
      className={classNames("quotation-wrapper", props.class, {
        lightBackground: sectionBackgroundShade === "light",
      })}
    >
      <div className="quotation-details">
        <a.div className="quotationMark" style={markStyles}>
          &ldquo;
        </a.div>
        <a.h1 style={titleStyles}>{quotation.quotation}</a.h1>
        <div className="detailsWrapper">
          <a.div className="detailsContainer" style={detailsContainerStyles}>
            <div className="lineWrapper">
              <div className="lineContainer">
                <a.div className="line" style={lineStyles} />
              </div>
            </div>
            <div className="personContainer">
              <a.div className="name" style={nameStyles}>
                {quotation.name}
              </a.div>
              <a.div className="title" style={personTitleStyles}>
                {quotation.title}
              </a.div>
            </div>
          </a.div>
        </div>
      </div>
    </div>
  );
};

export const FullQuotation = FullQuotationComponent;
